import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SimulationsLaufProvider } from './hooks/useSimulationsLauf';
import { SupportZugriffProvider } from './hooks/useSupportZugriff';
import { SiteConfigProvider, useSiteConfig } from './hooks/useSiteConfig';
import { LanguageProvider, useLanguage } from './hooks/useLanguage';
import { useTranslation } from './hooks/useTranslation';
import ImpersonationBanner from './components/ImpersonationBanner';
import TranslationLoadErrorBanner from './components/TranslationLoadErrorBanner';
import VerbindungsHinweis from './components/VerbindungsHinweis';
import { SupportChatLoader } from './shared/SupportChatLoader';
import ConsentBanner from './components/ConsentBanner';
import DarstellungKonto from './components/DarstellungKonto';
import PlausibleAnalytics from './components/PlausibleAnalytics';
import OeffentlicherRahmen, { HINWEIS_MITTE } from './components/OeffentlicherRahmen';
import { istRechtsPfad } from './utils/legal';

/**
 * Was um JEDE Seite liegt — die Provider und die globalen Bausteine.
 *
 * Bis zur Next.js-Phase 2 (03.10.2026) stand das in `App.jsx`. Seitdem gibt es
 * zwei Wege, auf denen eine Seite gerendert wird: die Single-Page-App
 * (`App.jsx`, im Browser) und die Server-Seiten (`OeffentlicheSeite.tsx`,
 * zuerst auf dem Server). Beide nehmen diesen Rahmen — sonst fehlten auf den
 * Landingpages, auf denen Besucher aus einer Kampagne ankommen, genau die
 * Teile, die dafür da sind: Plausible, die Herkunfts-Erfassung und der
 * Einwilligungs-Banner.
 *
 * Den Router bringt jeder Weg selbst mit (`BrowserRouter` bzw. ein Router, der
 * jeden Klick als echten Seitenaufruf ausführt).
 *
 * @param {{ startOrt?: { country: string, language: string } | null, children: import('react').ReactNode }} props
 *   `startOrt` nur für Server-Seiten, siehe `LanguageProvider`.
 */
export default function AppRahmen({ startOrt = null, children }) {
  return (
    <>
      <PlausibleAnalytics />
      <UtmCapture />
      <AuthProvider>
        <SiteConfigProvider>
          <LanguageProvider startOrt={startOrt}>
            {/* Über den Routen, nicht im Layout: Das Layout wird je Routen-Hülle
                neu angelegt, der laufende Test soll einen Seitenwechsel aber
                überleben. */}
            <SimulationsLaufProvider>
              <SupportZugriffProvider>
                <Globales>{children}</Globales>
              </SupportZugriffProvider>
            </SimulationsLaufProvider>
          </LanguageProvider>
        </SiteConfigProvider>
      </AuthProvider>
    </>
  );
}

// Alles unterhalb des LanguageProvider. Der Wartungsmodus wird hier
// entschieden, nicht eine Ebene darüber: ausserhalb des Providers fällt
// `useLanguage()` auf die Vorgabesprache zurück (und warnt in der Konsole),
// der Bildschirm stünde also für alle elf Sprachen auf Deutsch — und er ist
// in diesem Zustand das Einzige, was ein Nutzer überhaupt zu sehen bekommt.
function Globales({ children }) {
  const { isMaintenanceMode, loading } = useSiteConfig();
  const { user, sitzungsNutzer, loading: sitzungLaedt } = useAuth();
  const { language } = useLanguage();
  const { pathname } = useLocation();

  // Wartungsmodus (außer für Admins). Die Rechtstexte und das Kündigen/
  // Widerrufen bleiben erreichbar — die Footer-Links des Wartungsbildschirms
  // zeigen genau dorthin (Regel #21, §312k BGB). Beide Wege (SPA und
  // Server-Seiten) liefern einen Router, `useLocation` gibt es also immer.
  if (!loading && isMaintenanceMode && !user?.is_admin && !istRechtsPfad(pathname)) {
    return <MaintenanceScreen />;
  }

  return (
    <>
      {/* Meldet einen fehlgeschlagenen Abruf von /api/i18n/translations/<sprache>.
          Muss INSIDE LanguageProvider haengen, damit er jede Route abdeckt —
          ohne ihn laeuft die Anwendung stumm auf deutschen Default-Texten. */}
      <TranslationLoadErrorBanner />
      <ImpersonationBanner />
      {/* Server gerade nicht erreichbar: Der Nutzer bleibt angemeldet, die
          Leiste sagt es und bietet „Erneut versuchen“ (useAuth.sitzungPruefen). */}
      <VerbindungsHinweis />
      {/* Support-Chat von 121assist (Fundament-Baustein shared/SupportChatLoader,
          FLEET_SUPPORT_CHAT_SPEC): ein Widget für Gäste, eins für Angemeldete.
          Im Rahmen statt im Layout, damit er auch auf Seiten ohne Layout
          (Anmeldung, Registrierung) greift. Angemeldet zählt nur die
          bestätigte Sitzung, nie der Cache — bis dahin `null`, der Lader wartet. */}
      <SupportChatLoader angemeldet={sitzungLaedt ? null : !!sitzungsNutzer} sprache={language} />
      {/* Einmal für alle Seiten — auch die öffentlichen, auf denen Besucher
          aus einer Kampagne landen. Rendert nichts, wenn schon entschieden. */}
      <ConsentBanner />
      {/* Hell/dunkel am Konto: übernimmt nach der Anmeldung, speichert beim Umschalten. */}
      <DarstellungKonto />
      {children}
    </>
  );
}

// Maintenance Screen. Wird INNERHALB des LanguageProvider entschieden (siehe
// Globales) — sonst liefe `useTranslation()` hier auf der Vorgabesprache und
// jeder Nutzer bekäme den deutschen Text, ausgerechnet auf dem einen Bildschirm,
// der in diesem Zustand alles ist, was er zu sehen bekommt.
function MaintenanceScreen() {
  const { config } = useSiteConfig();
  const { t } = useTranslation();

  // Ohne Kopf (Anmelden führt im Wartungsmodus nirgends hin), aber mit
  // Legal-Footer: Impressum und Datenschutz müssen auch jetzt erreichbar sein
  // — deshalb lässt `Globales` deren Pfade am Wartungsmodus vorbei.
  return (
    <OeffentlicherRahmen kopf={false} inhaltStyle={HINWEIS_MITTE}>
      <h1 style={{ fontSize: '3rem' }}>🔧</h1>
      <h2 style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>
        {t('app.maintenance_title', 'Wartungsarbeiten')}
      </h2>
      {/* Ein im Admin gepflegter Text ist eine bewusste Ansage für diesen einen
          Ausfall und geht der allgemeinen Formulierung vor — kein Rückfall,
          der einen Fehler verdeckt. */}
      <p style={{ color: 'var(--text-secondary)', maxWidth: '400px' }}>
        {config.maintenance_message
          || t('app.maintenance_message', 'Wir führen gerade Wartungsarbeiten durch. Bitte versuch es später erneut.')}
      </p>
    </OeffentlicherRahmen>
  );
}

function UtmCapture() {
  // Herkunft erfassen: 30-Tage-Speicher fuer die Registrierung, dazu das
  // Consent-gebundene Aufzeichnen einzelner Besuche.
  //
  // Hier lief bis 09/2026 ein ZWEITES Erfassungssystem (`useUtmTracking`,
  // sessionStorage). Es schrieb `paperball_utm_*` bei jedem Start — und
  // niemand hat je gelesen: `getUtmParams` war im ganzen Frontend ohne
  // Aufrufer. Zwei Systeme fuer dieselbe Frage, eines davon tot.
  useEffect(() => {
    import('./lib/attribution')
      .then(mod => mod.initAttribution(''))
      // Bis 03.10.2026 ein leeres `catch`: Ein Ladefehler hier heisst, dass
      // keine Kampagne mehr zugeordnet wird — das darf nicht still passieren.
      .catch((err) => console.error('Herkunfts-Erfassung nicht ladbar:', err));
  }, []);
  return null;
}
